import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Twitter, Send, Copy } from "lucide-react";
import { BloqStacked } from "@/components/brand/BloqLogo";
import { WaitlistForm } from "@/components/lander/WaitlistForm";
import { LAUNCH_DATE } from "@/config";

function calc() {
  const diff = Math.max(0, new Date(LAUNCH_DATE).getTime() - Date.now());
  return {
    d: Math.floor(diff / 86400000),
    h: Math.floor(diff / 3600000) % 24,
    m: Math.floor(diff / 60000) % 60,
    s: Math.floor(diff / 1000) % 60,
  };
}

function Unit({ value, label }) {
  return (
    <div className="flex flex-col items-center">
      <div className="flex h-20 w-20 items-center justify-center rounded-2xl border border-white/10 bg-bloq-s1/80 font-heading text-3xl font-bold tabular-nums backdrop-blur sm:h-24 sm:w-24 sm:text-4xl">
        {String(value).padStart(2, "0")}
      </div>
      <span className="mt-2 text-[11px] font-semibold uppercase tracking-widest text-bloq-muted">{label}</span>
    </div>
  );
}

// Variant 4 — "Vault": countdown + gamified waitlist position.
export default function Vault() {
  const [t, setT] = useState(calc());
  const [joined, setJoined] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const id = setInterval(() => setT(calc()), 1000);
    return () => clearInterval(id);
  }, []);

  const share = (network) => {
    const base = process.env.REACT_APP_SHARE_BASE || "https://bloqtalk.app";
    const code = result?.referral_code;
    const link = code ? `${base}/?ref=${code}` : base;
    const text = encodeURIComponent("I just joined the BLOQ Talk waitlist — the trust-gated messenger for Web3. Get early access:");
    const url = encodeURIComponent(link);
    if (network === "x") window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, "_blank");
    else if (network === "tg") window.open(`https://t.me/share/url?url=${url}&text=${text}`, "_blank");
    else {
      navigator.clipboard?.writeText(link);
      toast.success("Referral link copied");
    }
  };

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-bloq-bg px-5 py-10 text-center text-white">
      <div className="pointer-events-none absolute inset-0 bloq-gradient opacity-[0.10]" />
      <div className="pointer-events-none absolute inset-0 grain opacity-50" />
      <div className="pointer-events-none absolute -top-24 left-1/2 h-[520px] w-[720px] -translate-x-1/2 rounded-full bg-bloq-blue/12 blur-[150px]" />

      <div className="relative z-10 flex w-full max-w-xl flex-col items-center">
        <BloqStacked size={200} className="animate-fade-up" />

        <p className="mt-7 text-xs font-bold uppercase tracking-[0.3em] text-bloq-mint">Launching in</p>
        <div className="mt-4 flex items-center gap-3 sm:gap-4">
          <Unit value={t.d} label="Days" />
          <Unit value={t.h} label="Hrs" />
          <Unit value={t.m} label="Min" />
          <Unit value={t.s} label="Sec" />
        </div>

        {joined ? (
          <div className="mt-9 w-full max-w-md animate-fade-in rounded-2xl border border-bloq-mint/40 bg-bloq-mint/10 p-6" data-testid="waitlist-success">
            {result?.position ? (
              <>
                <div className="text-sm text-bloq-muted">You're in line at position</div>
                <div className="font-heading text-4xl font-bold text-bloq-mint" data-testid="waitlist-position">#{result.position.toLocaleString()}</div>
              </>
            ) : (
              <div className="text-base font-semibold text-bloq-mint">You're on the list!</div>
            )}
            {result?.referral_count > 0 && (
              <div className="mt-1 text-xs text-bloq-mint">
                {result.referral_count} referral{result.referral_count === 1 ? "" : "s"} · {(result.referral_count * result.spots_per_referral).toLocaleString()} spots earned
              </div>
            )}
            <div className="mt-4 text-sm text-white/80">
              Jump the queue — invite friends{result?.spots_per_referral ? ` (each = ${result.spots_per_referral} spots)` : ""}:
            </div>
            <div className="mt-3 flex items-center justify-center gap-3">
              <button onClick={() => share("x")} className="flex h-11 w-11 items-center justify-center rounded-full border border-white/12 hover:bg-white/5" data-testid="share-x-btn"><Twitter className="h-5 w-5" /></button>
              <button onClick={() => share("tg")} className="flex h-11 w-11 items-center justify-center rounded-full border border-white/12 hover:bg-white/5" data-testid="share-tg-btn"><Send className="h-5 w-5" /></button>
              <button onClick={() => share("copy")} className="flex h-11 w-11 items-center justify-center rounded-full border border-white/12 hover:bg-white/5" data-testid="share-copy-btn"><Copy className="h-5 w-5" /></button>
            </div>
            <div className="mt-4 rounded-xl border border-bloq-mint/30 bg-bloq-mint/[0.08] px-3 py-2 text-xs font-medium text-bloq-mint" data-testid="founding-reserved">
              🎉 Founding perks reserved — 50% off BLOQ+ for life + the exclusive Founding badge, unlocked at launch.
            </div>
          </div>
        ) : (
          <div className="mt-9 w-full max-w-md">
            <p className="mb-4 text-base text-bloq-muted">Reserve your handle. Be first through the gate.</p>
            <WaitlistForm founding showSuccess={false} onDone={(r) => { setResult(r); setJoined(true); }} />
          </div>
        )}

        <div className="mt-10 flex items-center gap-5 text-xs text-bloq-muted">
          <Link to="/privacy" className="hover:text-white" data-testid="lander-privacy-link">Privacy</Link>
          <Link to="/terms" className="hover:text-white" data-testid="lander-terms-link">Terms</Link>
          <Link to="/data-security" className="hover:text-white" data-testid="lander-security-link">Data Security</Link>
          <Link to="/delete-account" className="hover:text-white" data-testid="lander-delete-link">Delete Account</Link>
          <span>© {new Date().getFullYear()} BLOQ Talk</span>
        </div>
      </div>
    </div>
  );
}
